// Diagnóstico e achados estruturados de uma run, derivados SÓ das métricas
// já disponíveis (TestRunMetrics). Não chama LLM, não altera dados. Alimenta o
// bloco "Diagnóstico da run", a seção "Top problemas" e os cards da aba Insights.

import type { TestRunMetrics } from '@sophia/shared-schemas'
import { falseNegatives, falsePositives } from './run-comparison'

export type Severity = 'ALTA' | 'MEDIA' | 'BAIXA'
export type DiagTone = 'success' | 'warning' | 'danger' | 'neutral'

export interface RunDiagnosis {
  tone: DiagTone
  headline: string
  detail: string
}

export interface Finding {
  id: string
  kind: 'rule' | 'confusion' | 'unclassified' | 'conflict'
  problem: string
  evidence: string
  impact: string
  action: string
  severity: Severity
  score: number
}

function pct(v: number): string {
  return `${(v * 100).toFixed(1)}%`
}

// ---- Diagnóstico curto, orientado à decisão ----
export function diagnoseRun(m: TestRunMetrics): RunDiagnosis {
  if (m.classified === 0) {
    return {
      tone: 'neutral',
      headline: 'Nenhuma linha foi classificada pelas regras',
      detail: 'Toda a base caiu pro LLM. Crie regras a partir dos sinais antes de avaliar precisão.',
    }
  }
  const p = m.precisionGlobal
  const c = m.coverageGlobal

  if (p === 0) {
    return {
      tone: 'danger',
      headline: 'Crítico: as regras classificam, mas erram tudo',
      detail: `Precisão 0% com ${pct(c)} de cobertura. Verifique o vínculo das classes esperadas e os padrões antes de iterar.`,
    }
  }
  if (p >= 0.8 && c >= 0.8) {
    return {
      tone: 'success',
      headline: 'Run saudável: boa precisão e boa cobertura',
      detail: `Precisão ${pct(p)} e cobertura ${pct(c)}. Forte candidata a considerar produção — revise apenas os poucos problemas abaixo.`,
    }
  }
  if (p >= 0.7 && c < 0.5) {
    return {
      tone: 'warning',
      headline: 'Boa precisão, mas cobertura baixa',
      detail: `Acerta quando classifica (${pct(p)}), mas só cobre ${pct(c)} da base. Priorize criar regras pras publicações sem classificação.`,
    }
  }
  if (p < 0.5 && c >= 0.7) {
    return {
      tone: 'danger',
      headline: 'Cobertura alta, mas precisão baixa',
      detail: `Classifica ${pct(c)} da base, mas com precisão ${pct(p)}. Priorize refinar as regras que mais geram falso positivo.`,
    }
  }
  return {
    tone: 'warning',
    headline: 'Resultado mediano',
    detail: `Precisão ${pct(p)} e cobertura ${pct(c)}. Ataque os problemas de maior impacto abaixo antes de promover esta configuração.`,
  }
}

function severityFromCount(count: number, high: number, mid: number): Severity {
  if (count >= high) return 'ALTA'
  if (count >= mid) return 'MEDIA'
  return 'BAIXA'
}

// ---- Achados estruturados, ordenados por impacto ----
export function deriveFindings(m: TestRunMetrics, limit = 6): Finding[] {
  const findings: Finding[] = []

  // 1. Regras de baixa precisão com volume relevante
  for (const r of m.perRule) {
    if (r.matches < 3 || r.precision >= 0.9) continue
    findings.push({
      id: `rule-${r.ruleId}`,
      kind: 'rule',
      problem: `Regra "${r.ruleName}" (${r.className}) com precisão ${pct(r.precision)}`,
      evidence: `${r.matches} matches · ${r.falsePositives} falsos positivos`,
      impact: `Puxa a precisão de ${r.className} pra baixo e gera ruído na classificação.`,
      action: 'Refine o padrão, adicione uma exclusão ou reduza o peso da regra.',
      severity: severityFromCount(r.falsePositives, 10, 3),
      score: r.falsePositives * 10,
    })
  }

  // 2. Padrões de erro sistemático (confusão fora da diagonal)
  for (const cell of m.confusion) {
    if (cell.predictedClassId === null) continue // não classificado tratado à parte
    if (cell.expectedClassId === cell.predictedClassId) continue // acerto
    findings.push({
      id: `conf-${cell.expectedClassId ?? 'null'}-${cell.predictedClassId}`,
      kind: 'confusion',
      problem: `Textos de "${cell.expectedClassName}" sendo classificados como "${cell.predictedClassName}"`,
      evidence: `${cell.count} ocorrências`,
      impact: `Falso positivo em ${cell.predictedClassName} e falso negativo em ${cell.expectedClassName}.`,
      action: `Adicione exclusão na regra de ${cell.predictedClassName} ou reforce os sinais de ${cell.expectedClassName}.`,
      severity: severityFromCount(cell.count, 10, 3),
      score: cell.count * 8,
    })
  }

  // 3. Volume não classificado (lacuna de cobertura)
  if (m.notClassified > 0) {
    findings.push({
      id: 'unclassified',
      kind: 'unclassified',
      problem: `${m.notClassified} publicações sem classificação`,
      evidence: `${pct(m.llmRate)} da base caiu pro LLM`,
      impact: 'Cobertura menor e custo recorrente de LLM no pipeline.',
      action: 'Crie regras pros padrões não cobertos (aba Predições › Não classificadas).',
      severity: m.llmRate >= 0.5 ? 'ALTA' : m.llmRate >= 0.25 ? 'MEDIA' : 'BAIXA',
      score: m.notClassified * 4,
    })
  }

  // 4. Conflitos de classe
  if (m.conflictRows > 0) {
    findings.push({
      id: 'conflict',
      kind: 'conflict',
      problem: `${m.conflictRows} linhas com conflito de classe`,
      evidence: `${pct(m.conflictRate)} casaram em 2+ classes ao mesmo tempo`,
      impact: 'O resultado dessas linhas depende de desempate por peso — frágil.',
      action: 'Adicione exclusões mútuas entre as classes que competem ou ajuste os pesos.',
      severity: m.conflictRate >= 0.2 ? 'ALTA' : m.conflictRate >= 0.05 ? 'MEDIA' : 'BAIXA',
      score: m.conflictRows * 3,
    })
  }

  return findings.sort((a, b) => b.score - a.score).slice(0, limit)
}

// Reexport pra quem quiser os totais globais de FP/FN.
export { falsePositives, falseNegatives }
